'use client';

import { Sheet, SheetContent, SheetTitle } from '@ahafood/ui/components/sheet';
import { useCallback, useEffect, useState } from 'react';
import OrderNotifications from './page';
import { useIsMobile } from '@/lib/is-mobile';

interface OrderListSheetProps {
  orderId: string | null;
  isOpen: boolean;
  onClose: () => void;
}

export default function OrderListSheet({
  orderId,
  isOpen,
  onClose,
}: OrderListSheetProps) {
  const [open, setOpen] = useState(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    setOpen(isOpen);
  }, [isOpen]);

  // Close sheet when tab visibility changes to prevent overlay issues
  useEffect(() => {
    if (!isOpen) return;

    const handleVisibilityChange = () => {
      if (document.hidden) {
        console.log('[OrderListSheet] Document hidden, closing sheet');
        onClose();
      }
    };

    const handleBeforeUnload = () => {
      if (open) {
        onClose();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isOpen, open, onClose]);

  const handleOpenChange = useCallback(
    (value: boolean) => {
      if (!value) {
        const isInTransition =
          document.documentElement.dataset.drawerToSheetTransition === 'true';
        if (!isInTransition) {
          onClose();
        }
      }
      setOpen(value);
    },
    [onClose]
  );

  if (!isOpen && !orderId) return null;

  // Mobile Sheet version
  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent
        className="size-full md:w-[450px] max-w-lg mx-auto pt-0 px-0 bg-white flex flex-col ios-webview-fix overflow-y-auto z-50"
        side={isMobile ? 'bottom' : 'right'}
        style={{ WebkitOverflowScrolling: 'touch' }}
        onClick={(e) => e.stopPropagation()}
      >
        <SheetTitle className="text-xl font-semibold hidden">
          Chi tiết đơn hàng
        </SheetTitle>
        <OrderNotifications orderId={orderId} onClose={onClose} />
      </SheetContent>
    </Sheet>
  );
}
